import type { User } from '@repo-level/core-front';
import {
  LEVELS,
  PROGRESS_STEPS,
  SCHEDULER_GROUP_PATTERN,
  STATUS_GROUP_PATTERN,
} from '../constants';
import type { ELevelKey, IStatusGroup } from '../types';
import { EProgressStep } from '../types';

const levelOrderByKey = new Map<string, number>(
  LEVELS.map(({ key, order }) => [key, order])
);

const steps = PROGRESS_STEPS as readonly number[];

const isLevelKey = (value: string): value is ELevelKey =>
  levelOrderByKey.has(value);

const isProgressStep = (value: number): value is EProgressStep =>
  steps.includes(value);

const parseGroupId = (statusId: string): IStatusGroup | null => {
  const match = STATUS_GROUP_PATTERN.exec(statusId.toLowerCase());

  if (!match) return null;

  const [, levelKey, rawStep] = match;
  const step = Number.parseInt(rawStep, 10);

  if (!(isLevelKey(levelKey) && isProgressStep(step))) return null;

  return { levelKey, step };
};

const getOrder = ({ levelKey }: IStatusGroup): number =>
  levelOrderByKey.get(levelKey) ?? 0;

const isFurther = (group: IStatusGroup, than: IStatusGroup): boolean => {
  const orderDiff = getOrder(group) - getOrder(than);

  return orderDiff === 0 ? group.step > than.step : orderDiff > 0;
};

const parseUserGroups = (user?: User): IStatusGroup[] =>
  (user?.statuses ?? []).flatMap(({ id }) => parseGroupId(id) ?? []);

/**
 * The milestones of completed levels stay next to the group of the current
 * one, so the furthest group is where the player is now.
 */
export const resolveStatusGroup = (user?: User): IStatusGroup | null =>
  parseUserGroups(user).reduce<IStatusGroup | null>(
    (furthest, group) =>
      !furthest || isFurther(group, furthest) ? group : furthest,
    null
  );

/** order of the highest level with its 100% milestone this week */
export const getWeeklyTier = (user?: User): number | null =>
  parseUserGroups(user)
    .filter(({ step }) => step === EProgressStep.COMPLETE)
    .reduce<number | null>(
      (tier, group) => Math.max(tier ?? 0, getOrder(group)),
      null
    );

/** order of the level the weekly snapshot has fixed the reward for */
export const getSchedulerTier = (user?: User): number | null =>
  (user?.statuses ?? []).reduce<number | null>((tier, { id }) => {
    const match = SCHEDULER_GROUP_PATTERN.exec(id.toLowerCase());

    return match ? Math.max(tier ?? 0, Number.parseInt(match[1], 10)) : tier;
  }, null);
